import React, { useCallback, useEffect, useRef, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { FaHeadset, FaHome, FaPaperPlane, FaRobot } from "react-icons/fa";
import BackButton from "../ChildComponent/BackButton";
import MyLoader from "../Spinner/MyLoader";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import {
  closeSupportChat,
  loadSupportChatSettings,
  loadSupportThread,
  requestSupportHuman,
  sendSupportMessage,
  supportStaffContext,
} from "../Utils/supportChatApi";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

function bubbleStyle(role) {
  if (role === "school") {
    return { bg: COLORS.primary_color_shade7, color: "#fff", align: "flex-end" };
  }
  if (role === "crm") {
    return { bg: "#E8F5E9", color: "#1B5E20", align: "flex-start" };
  }
  return { bg: "#fff", color: "#263238", align: "flex-start", border: "1px solid #E1F5FE" };
}

// Created By Cursor
export default function SupportChat() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({
    service_enabled: true,
    ai_enabled: false,
  });
  const [serviceOff, setServiceOff] = useState(false);
  const listRef = useRef(null);
  const pollRef = useRef(null);

  const mode = String(chat?.mode || (settings.ai_enabled ? "AI" : "HUMAN")).toUpperCase();
  const serviceOn = settings.service_enabled !== false && !serviceOff;

  const applyThread = useCallback((data) => {
    if (!data) return;
    if (data.settings) {
      setSettings({
        service_enabled: data.settings.service_enabled !== false,
        ai_enabled: !!data.settings.ai_enabled,
      });
    }
    if (data.api_status !== "OK") {
      if (/turned off/i.test(String(data.api_remark || ""))) {
        setServiceOff(true);
      }
      return;
    }
    setServiceOff(false);
    setChat(data.chat || null);
    setMessages(Array.isArray(data.messages) ? data.messages : []);
  }, []);

  const refresh = useCallback(async ({ soft = false } = {}) => {
    const ctx = supportStaffContext();
    if (!ctx.school_code || !ctx.staff_id) {
      setLoading(false);
      return;
    }
    if (!soft) setLoading(true);
    try {
      const data = await loadSupportThread();
      if (data.api_status === "OK") {
        applyThread(data);
      } else {
        applyThread(data);
        const remark = String(data.api_remark || "Could not load chat.");
        if (!/turned off/i.test(remark)) {
          myToast.notify_warning(remark);
        }
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning(
        error?.response?.status === 404
          ? "Support chat API is missing on the server. Deploy the PHP files."
          : "Could not load support chat."
      );
    } finally {
      setLoading(false);
    }
  }, [applyThread]);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      const data = await loadSupportChatSettings();
      if (data.api_status === "OK") {
        setSettings({
          service_enabled: data.settings?.service_enabled !== false,
          ai_enabled: !!data.settings?.ai_enabled,
        });
        setServiceOff(data.settings?.service_enabled === false);
      }
      await refresh();
    })();
    pollRef.current = setInterval(() => refresh({ soft: true }), 8000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [refresh]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy || mode === "CLOSED") return;
    const ctx = supportStaffContext();
    if (!ctx.session_id && mode === "AI") {
      myToast.notify_warning("Open the dashboard once so the session is loaded, then ask again.");
      return;
    }
    setDraft("");
    setBusy(true);
    try {
      const data = await sendSupportMessage(text);
      if (data.api_status === "OK") applyThread(data);
      else myToast.notify_warning(data.api_remark || "Could not send.");
    } catch (error) {
      console.error(error);
      myToast.notify_warning(
        error?.response?.status === 404
          ? "Support chat API is missing on the server. Deploy the PHP files."
          : "Could not send message."
      );
    } finally {
      setBusy(false);
    }
  };

  const handoff = async () => {
    if (busy || mode === "HUMAN" || mode === "CLOSED") return;
    setBusy(true);
    try {
      const data = await requestSupportHuman(chat?.chat_id);
      if (data.api_status === "OK") {
        applyThread(data);
        myToast.notify_success("Support has been notified.");
      } else {
        myToast.notify_warning(data.api_remark || "Could not request support.");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Could not request support.");
    } finally {
      setBusy(false);
    }
  };

  const onClose = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (chat?.chat_id && mode !== "CLOSED") {
        await closeSupportChat(chat.chat_id);
      }
      await refresh();
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Could not close chat.");
    } finally {
      setBusy(false);
    }
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  const banner = !serviceOn
    ? "Support chat is turned off by EduOK support."
    : mode === "HUMAN"
      ? chat?.assigned_admin_name
        ? `Talking with ${chat.assigned_admin_name}. AI is paused.`
        : "Human support — send your question and our team will reply."
      : mode === "CLOSED"
        ? "Previous chat closed. A new chat will open when you send a message."
        : "AI mode — CRM can monitor this thread. Use Talk to support for a human.";

  return (
    <div className="content payroll-page sm-page" style={{ fontFamily: 'Inter, system-ui, "Segoe UI", Roboto, Arial, sans-serif' }}>
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <BackButton />
            </li>
            <li>
              <small>Support Chat</small>
            </li>
          </ul>
        </div>
      </div>

      <div className="content-body" style={{ padding: 16 }}>
        <div
          style={{
            maxWidth: 860,
            margin: "0 auto",
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #E1F5FE",
            boxShadow: "0 8px 24px rgba(1,87,155,0.08)",
            display: "flex",
            flexDirection: "column",
            minHeight: "70vh",
          }}
        >
          <div
            style={{
              padding: "14px 16px",
              background: COLORS.primary_color_shade7,
              color: "#fff",
              borderRadius: "16px 16px 0 0",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <FaRobot />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>Support Chat</div>
              <div style={{ fontSize: 12, opacity: 0.9 }}>
                Fees · Students · Academics · Human handoff
              </div>
            </div>
            {settings.ai_enabled ? (
              <button
                type="button"
                onClick={handoff}
                disabled={busy || mode === "HUMAN" || mode === "CLOSED" || !serviceOn}
                style={{
                  border: 0,
                  borderRadius: 8,
                  padding: "6px 10px",
                  background: "#fff",
                  color: COLORS.primary_color_shade9,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  opacity: mode === "HUMAN" || mode === "CLOSED" || !serviceOn ? 0.6 : 1,
                }}
              >
                <FaHeadset /> Talk to support
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={{
                border: "1px solid rgba(255,255,255,0.5)",
                borderRadius: 8,
                padding: "6px 10px",
                background: "transparent",
                color: "#fff",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              {mode === "CLOSED" ? "New chat" : "Close"}
            </button>
          </div>

          <div
            style={{
              padding: "8px 14px",
              fontSize: 12,
              background: mode === "HUMAN" ? "#FFF8E1" : "#E3F2FD",
              color: "#37474F",
            }}
          >
            {banner}
          </div>

          <div ref={listRef} style={{ flex: 1, overflowY: "auto", padding: 16, background: "#F7FBFE", minHeight: 360 }}>
            {!serviceOn ? (
              <p style={{ color: "#546E7A", margin: 0 }}>
                Support chat is currently unavailable. Please use Help Desk contacts or try again later.
              </p>
            ) : loading ? (
              <MyLoader />
            ) : (
              messages.map((message) => {
                const role = message.sender_role || "ai";
                const style = bubbleStyle(role);
                return (
                  <div
                    key={message.message_id}
                    style={{ display: "flex", justifyContent: style.align, marginBottom: 10 }}
                  >
                    <div
                      style={{
                        maxWidth: "75%",
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: style.bg,
                        color: style.color,
                        border: style.border || "none",
                        whiteSpace: "pre-wrap",
                        fontSize: 14,
                        lineHeight: 1.45,
                      }}
                    >
                      {role !== "school" ? (
                        <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4 }}>
                          {role === "crm" ? message.sender_name || "Support" : "School AI"}
                          {message.date_added ? ` · ${message.date_added}` : ""}
                        </div>
                      ) : null}
                      {message.message_text}
                    </div>
                  </div>
                );
              })
            )}
            {busy ? <div style={{ color: "#0288D1", fontSize: 13 }}>Working…</div> : null}
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              send();
            }}
            style={{ display: "flex", gap: 8, padding: 12, borderTop: "1px solid #E1F5FE" }}
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                !serviceOn
                  ? "Chat is turned off"
                  : mode === "HUMAN"
                    ? "Message support…"
                    : "Ask about fees, students, academics…"
              }
              disabled={busy || !serviceOn}
              style={{
                flex: 1,
                border: "1px solid #B3E5FC",
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 14,
                outline: "none",
              }}
            />
            <button
              type="submit"
              disabled={busy || !draft.trim() || !serviceOn}
              style={{
                border: 0,
                borderRadius: 10,
                width: 46,
                background: COLORS.primary_color_shade7,
                color: "#fff",
                cursor: busy || !draft.trim() || !serviceOn ? "default" : "pointer",
                opacity: busy || !draft.trim() || !serviceOn ? 0.6 : 1,
              }}
              aria-label="Send"
            >
              <FaPaperPlane />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
